import React, { useState, useEffect, useRef } from 'react';
import { Link } from 'react-router-dom';
import { Button } from 'react-bootstrap';
import { toast } from 'react-toastify';
import { v4 as uuidv4 } from 'uuid';
import FormSelectBox from '../../../../../components/Form/FormSelectBox';
import FormInput from '../../../../../components/Form/FormInput';
import Table, { ColumnDefinitionType } from '../../../../../components/Table';
import MyModal from '../../../../../components/Modal';
import { usePersonalHabilitacion } from '../../../../../hooks/usePersonalHabilitacion';
import { useHabilitacionContratista } from '../../../../../hooks/useHabilitacionContratista';
import { PersonalHabilitacion } from '../../../../../types/PersonalHabilitacion';
import { DocumentoPersonalHabilitacion } from '../../../../../types/DocumentoPersonalHabilitacion';
import { findPersonalByTipoAndNumDoc, findAll } from '../../../../../services/Service';
import { useDocumentoPersonalHabilitacion } from '../../../../../hooks/useDocumentoPersonalHabilitacion';
import { useUserLogin } from '../../../../../hooks/useUserLogin';
import { useCalificacionHabilitacion } from '../../../../../hooks/useCalificacionHabilitacion';
import ObservacionComponent from '../../../../../components/Observaciones';
import {
    EN_PROCESO_STATUS_ID,
    HAB_REVISION_STATUS_ID,
    HAB_OBSERVADA_STATUS_ID,
    OK_DOCUMENTO_STATUS,
    NO_OK_DOCUMENTO_STATUS,
} from '../../../../../constants/constants';
import moment from 'moment';

const SITUACIONES_CONTRACTUALES = [
    { id: 'PROPIO', descripcion: 'Personal Propio' },
    { id: 'SUBCONTRATADO', descripcion: 'Personal Subcontratado' },
    { id: 'AUTONOMO', descripcion: 'Personal Autónomo' },
];

interface DocPendiente {
    uid: string;
    idTipoDocumento: number;
    nombreTipoDocumento: string;
    file: File;
    fechaVencimiento: string;
    isFechaEditable: boolean;
}

type AgregarPersonaHabilitacionProps = {
    tiposDocPersona: any[];
    cancelButtonFn?: () => void;
    saveCompletedButtonFn?: () => void;
    isDisabled?: boolean;
    soloLectura?: boolean;
};

export const AgregarPersonaHabilitacion: React.FC<AgregarPersonaHabilitacionProps> = ({
    tiposDocPersona,
    cancelButtonFn,
    saveCompletedButtonFn,
    isDisabled = false,
    soloLectura = false,
}) => {
    const { habilitacionSelected, buscarHabilitacion } = useHabilitacionContratista();
    const {
        personalSelected,
        saveOrUpdatePersonal,
        buscarPersonalByIdHabilitacion,
    } = usePersonalHabilitacion();
    const {
        buscarDocumentosByPersonal,
        subirDocumento: subirDocumentoPersonal,
        visualizarDocumento: visualizarDocumentoPersonal,
        descargarDocumento: descargarDocumentoPersonal,
        eliminarDocumento: eliminarDocumentoPersonal,
    } = useDocumentoPersonalHabilitacion();
    const {
        isEmpresaGestoraTipoUsuario,
        isEmpresaSupervisoraTipoUsuario,
        isContratistaTipoUsuario,
    } = useUserLogin();
    const { calificarDocumentoPersonal } = useCalificacionHabilitacion();

    const estadoActual = habilitacionSelected.idTipoEstado;
    const puedeCalificar =
        !soloLectura &&
        (isEmpresaGestoraTipoUsuario() || isEmpresaSupervisoraTipoUsuario()) &&
        estadoActual === HAB_REVISION_STATUS_ID;
    const esObservadaContratista =
        isContratistaTipoUsuario() && estadoActual === HAB_OBSERVADA_STATUS_ID;

    const [docAEliminar, setDocAEliminar] = useState<any | null>(null);
    const [isOpenObservacionModal, setIsOpenObservacionModal] = useState(false);
    const [docObservacionSelected, setDocObservacionSelected] =
        useState<DocumentoPersonalHabilitacion | null>(null);

    const [form, setForm] = useState<any>({
        id: 0,
        idPersonal: 0,
        idTipoDocPersona: '',
        nroDocumento: '',
        apellido: '',
        nombre: '',
        situacionContractual: 'PROPIO',
        idHabilitacionContratista: habilitacionSelected.id,
    });
    const [nombreApellidoDisabled, setNombreApellidoDisabled] = useState(false);
    const [searching, setSearching] = useState(false);
    const btnRef = useRef<HTMLButtonElement>(null);

    const [tiposDocumento, setTiposDocumento] = useState<any[]>([]);
    const [isOpenDocModal, setIsOpenDocModal] = useState(false);
    const [documentosPendientes, setDocumentosPendientes] = useState<DocPendiente[]>([]);
    const [documentosGuardados, setDocumentosGuardados] = useState<DocumentoPersonalHabilitacion[]>([]);

    const [subIdTipoDocumento, setSubIdTipoDocumento] = useState<number | undefined>();
    const [subIsFechaEditable, setSubIsFechaEditable] = useState(false);
    const [subFechaVencimiento, setSubFechaVencimiento] = useState('');
    const [subSelectedFile, setSubSelectedFile] = useState<File | null>(null);
    const fileInputRef = useRef<HTMLInputElement>(null);

    useEffect(() => {
        findAll(`documentos-personal-contratista/find?idEmpresaGestora=${habilitacionSelected.idEmpresaGestora}&soloActivos=true`).then((response) => {
            if (response && response.data) setTiposDocumento(response.data);
        });
    }, [habilitacionSelected.idEmpresaGestora]);

    useEffect(() => {
        if (personalSelected.id > 0) {
            setForm({
                id: personalSelected.id,
                idPersonal: personalSelected.idPersonal || 0,
                idTipoDocPersona: personalSelected.idTipoDocPersona,
                nroDocumento: personalSelected.nroDocumento,
                apellido: personalSelected.apellido,
                nombre: personalSelected.nombre,
                situacionContractual: personalSelected.situacionContractual,
                idHabilitacionContratista: personalSelected.idHabilitacionContratista,
            });
            setNombreApellidoDisabled(true);
            buscarDocumentosByPersonal(personalSelected.id).then((response) => {
                if (response && response.data) setDocumentosGuardados(response.data);
            });
        }
    // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [personalSelected.id]);

    const handleInputChange = (name: string, value: string) => {
        setForm((prev: any) => ({ ...prev, [name]: value }));
    };

    const handleBuscarPersona = () => {
        if (!form.idTipoDocPersona || !form.nroDocumento || searching) return;
        setSearching(true);
        findPersonalByTipoAndNumDoc(Number(form.idTipoDocPersona), form.nroDocumento)
            .then((respuesta) => {
                setSearching(false);
                const data = respuesta?.data;
                if (data) {
                    setForm((prev: any) => ({
                        ...prev,
                        id: 0,
                        idPersonal: data.id || 0,
                        apellido: data.apellido || '',
                        nombre: data.nombre || '',
                    }));
                    setNombreApellidoDisabled(true);
                } else {
                    setForm((prev: any) => ({ ...prev, id: 0, idPersonal: 0, apellido: '', nombre: '' }));
                    setNombreApellidoDisabled(false);
                    setDocumentosGuardados([]);
                }
            })
            .catch(() => {
                setSearching(false);
                setForm((prev: any) => ({ ...prev, id: 0, idPersonal: 0, apellido: '', nombre: '' }));
                setNombreApellidoDisabled(false);
                setDocumentosGuardados([]);
            });
    };

    const handleNroDocumentoBlur = () => {
        if (form.idTipoDocPersona && form.nroDocumento) {
            handleBuscarPersona();
        }
    };

    const handleSubFileChange = () => {
        if (fileInputRef.current?.files?.[0]) {
            setSubSelectedFile(fileInputRef.current.files[0]);
        }
    };

    const resetDocModal = () => {
        setSubIdTipoDocumento(undefined);
        setSubIsFechaEditable(false);
        setSubFechaVencimiento('');
        setSubSelectedFile(null);
        if (fileInputRef.current) fileInputRef.current.value = '';
    };

    const handleSaveSubformDoc = () => {
        if (!subIdTipoDocumento) {
            toast.error('Seleccione el tipo de documento.');
            return;
        }
        if (!subSelectedFile) {
            toast.error('Seleccione un archivo.');
            return;
        }
        if (subIsFechaEditable && !subFechaVencimiento) {
            toast.error('Ingrese la fecha de vencimiento.');
            return;
        }
        const tipoDoc = tiposDocumento.find((t) => t.idTipoDocumento === subIdTipoDocumento);
        const newPendiente: DocPendiente = {
            uid: uuidv4(),
            idTipoDocumento: subIdTipoDocumento,
            nombreTipoDocumento: tipoDoc?.nombre || '',
            file: subSelectedFile,
            fechaVencimiento: subFechaVencimiento,
            isFechaEditable: subIsFechaEditable,
        };
        setDocumentosPendientes((prev) => {
            const yaExiste = prev.some((d) => d.idTipoDocumento === subIdTipoDocumento);
            return yaExiste
                ? prev.map((d) => (d.idTipoDocumento === subIdTipoDocumento ? newPendiente : d))
                : [...prev, newPendiente];
        });
        resetDocModal();
        setIsOpenDocModal(false);
    };

    const handleCancelDocModal = () => {
        resetDocModal();
        setIsOpenDocModal(false);
    };

    const handleRemovePendiente = (uid: string) => {
        setDocumentosPendientes((prev) => prev.filter((d) => d.uid !== uid));
    };

    const handleConfirmEliminarDoc = () => {
        const data = docAEliminar;
        if (!data) return;
        if (data.isPendiente) {
            handleRemovePendiente(data.uid);
        } else {
            eliminarDocumentoPersonal(data.id).then(() => {
                buscarDocumentosByPersonal(personalSelected.id).then((response) => {
                    if (response && response.data) setDocumentosGuardados(response.data);
                });
            }).catch(() => {
                toast.error('Error al eliminar el documento.');
            });
        }
        setDocAEliminar(null);
    };

    const handleSave = async () => {
        if (!form.idTipoDocPersona) { toast.error('Seleccione el tipo de documento.'); return; }
        if (!form.nroDocumento) { toast.error('Ingrese el número de documento.'); return; }
        if (!form.apellido) { toast.error('Ingrese el apellido.'); return; }
        if (!form.nombre) { toast.error('Ingrese el nombre.'); return; }
        if (!form.situacionContractual) { toast.error('Seleccione la situación contractual.'); return; }

        if (btnRef.current) btnRef.current.disabled = true;

        const personal: PersonalHabilitacion = {
            id: form.id || 0,
            idPersonal: form.idPersonal || 0,
            idTipoDocPersona: Number(form.idTipoDocPersona),
            tipoDocPersona: '',
            nroDocumento: form.nroDocumento,
            apellido: form.apellido,
            nombre: form.nombre,
            situacionContractual: form.situacionContractual,
            idHabilitacionContratista: habilitacionSelected.id,
            activo: true,
        };

        try {
            const result = await saveOrUpdatePersonal(personal);
            const personId = result?.data?.id || form.id || 0;

            for (const doc of documentosPendientes) {
                await subirDocumentoPersonal(doc.file, {
                    idTipoDocumento: doc.idTipoDocumento,
                    idPershabcontratista: personId,
                    fileId: doc.uid,
                    sizeMaximo: 20,
                    fechaVencimiento: doc.isFechaEditable ? doc.fechaVencimiento : null,
                });
            }

            buscarPersonalByIdHabilitacion(habilitacionSelected.id);
            // Refresca el estado de la habilitación: si estaba "Aprobada", al agregar personal
            // o documentación de personal el backend la pasó a "Actualizacion" (muestra "Enviar").
            buscarHabilitacion(String(habilitacionSelected.id));
            saveCompletedButtonFn?.();
        } catch {
            toast.error('Error al guardar el personal.');
            if (btnRef.current) btnRef.current.disabled = false;
        }
    };

    const onCheckboxChange = (data: any, status: string) => {
        if (data.isPendiente || typeof data.id !== 'number') return;
        setDocumentosGuardados((prevList) =>
            prevList.map((item) =>
                item.id === data.id ? { ...item, statusDocumento: status } : item
            )
        );
        calificarDocumentoPersonal(data.id, status, data.observacion ?? '').catch(() => {
            setDocumentosGuardados((prevList) =>
                prevList.map((item) =>
                    item.id === data.id
                        ? { ...item, statusDocumento: data.statusDocumento }
                        : item
                )
            );
        });
    };

    const onObservacionChange = (observacion: string) => {
        setIsOpenObservacionModal(false);
        if (!docObservacionSelected) return;
        const doc = docObservacionSelected;
        const observacionPrevia = doc.observacion;
        setDocumentosGuardados((prevList) =>
            prevList.map((item) =>
                item.id === doc.id ? { ...item, observacion } : item
            )
        );
        calificarDocumentoPersonal(doc.id, doc.statusDocumento ?? '', observacion).catch(() => {
            // revertir si falla
            setDocumentosGuardados((prevList) =>
                prevList.map((item) =>
                    item.id === doc.id
                        ? { ...item, observacion: observacionPrevia }
                        : item
                )
            );
        });
    };

    const docColumns: ColumnDefinitionType<any, any>[] = [
        { key: 'nombreTipoDocumento', header: 'Tipo Documento' },
        { key: 'nombreArchivo', header: 'Nombre' },
        {
            key: 'fechaVencimiento',
            header: 'Fecha Vencimiento',
            cell: (data: any) => data.fechaVencimiento ? moment(data.fechaVencimiento).format('DD/MM/YYYY') : '',
        },
        {
            key: 'id',
            header: 'Acciones',
            cell: (data: any) => (
                <>
                    {!data.isPendiente && (
                        <>
                            <Link
                                title="Visualizar"
                                to="#"
                                onClick={(e) => {
                                    e.preventDefault();
                                    visualizarDocumentoPersonal(data);
                                }}
                            >
                                <i className="fa fa-eye text-secondary me-2" aria-hidden="true" />
                            </Link>
                            <Link
                                title="Descargar"
                                to="#"
                                onClick={(e) => {
                                    e.preventDefault();
                                    descargarDocumentoPersonal(data);
                                }}
                            >
                                <i className="fa fa-arrow-down text-secondary me-2" aria-hidden="true" />
                            </Link>
                        </>
                    )}
                    {!isDisabled && (data.isPendiente || estadoActual === EN_PROCESO_STATUS_ID) && (
                        <a
                            href="#"
                            title="Eliminar"
                            onClick={(e) => {
                                e.preventDefault();
                                setDocAEliminar(data);
                            }}
                        >
                            <i className="fa fa-trash text-secondary me-2" aria-hidden="true" />
                        </a>
                    )}
                    {!data.isPendiente && puedeCalificar && (
                        <a
                            href="#"
                            title="Observaciones"
                            onClick={(e) => {
                                e.preventDefault();
                                setDocObservacionSelected(data);
                                setIsOpenObservacionModal(true);
                            }}
                        >
                            <i className="fa fa-search text-secondary me-2" aria-hidden="true" />
                        </a>
                    )}
                    {!data.isPendiente && esObservadaContratista && data.observacion && (
                        <a
                            href="#"
                            title="Observaciones"
                            onClick={(e) => {
                                e.preventDefault();
                                setDocObservacionSelected(data);
                                setIsOpenObservacionModal(true);
                            }}
                        >
                            <i className="fa fa-search me-2" style={{ color: 'red' }} aria-hidden="true" />
                        </a>
                    )}
                </>
            ),
        },
    ];

    if (puedeCalificar || esObservadaContratista || isDisabled) {
        docColumns.push({
            key: 'statusDocumento',
            header: '',
            isEnabledCheckStatusDocument: puedeCalificar,
            statusAprobadoValue: OK_DOCUMENTO_STATUS,
            statusNoAprobadoValue: NO_OK_DOCUMENTO_STATUS,
        });
    }

    const tiposPendientes = new Set(documentosPendientes.map((d) => d.idTipoDocumento));
    const allDocRows = [
        ...documentosGuardados.filter((d) => !tiposPendientes.has(d.idTipoDocumento)),
        ...documentosPendientes.map((d) => ({
            uid: d.uid,
            id: d.uid,
            nombreTipoDocumento: d.nombreTipoDocumento,
            nombreArchivo: d.file.name,
            fechaVencimiento: d.fechaVencimiento,
            isPendiente: true,
        })),
    ];

    return (
        <>
            {/* Modal: Confirmación de eliminación de documento */}
            <MyModal
                isOpen={!!docAEliminar}
                toggle={() => setDocAEliminar(null)}
                title="Advertencia"
                cancelButton={true}
                secondButton={true}
                secondButtonTitle={'Aceptar'}
                titleCancelar={'Cancelar'}
                secondButtonFn={handleConfirmEliminarDoc}
            >
                <label>¿Confirma la eliminación del registro?</label>
            </MyModal>
            {/* Modal: Observaciones del documento */}
            <MyModal
                isOpen={isOpenObservacionModal}
                toggle={() => setIsOpenObservacionModal(false)}
                title="Observaciones"
                cancelButton={false}
                secondButton={false}
            >
                <ObservacionComponent
                    observacion={docObservacionSelected?.observacion}
                    isEdit={puedeCalificar}
                    saveButtonFn={onObservacionChange}
                    cancelButtonFn={() => setIsOpenObservacionModal(false)}
                    returnButtonFn={() => setIsOpenObservacionModal(false)}
                />
            </MyModal>

            {/* Modal: Agregar Documento */}
            <MyModal
                isOpen={isOpenDocModal}
                toggle={handleCancelDocModal}
                title="Agregar Documento"
                enabledFooter={false}
            >
                <div className="form-horizontal">
                    <div className="row mt-2">
                        <FormSelectBox
                            title={'Tipo de Documento'}
                            label={'tipo de documento'}
                            id={'subTipoDocHab'}
                            name={'subIdTipoDocumento'}
                            optionId={'idTipoDocumento'}
                            optionLabel={'nombre'}
                            value={subIdTipoDocumento}
                            options={tiposDocumento}
                            onChange={(e) => {
                                const val = parseInt(e.target.value, 10);
                                setSubIdTipoDocumento(val);
                                const tipoDoc = tiposDocumento.find((t) => t.idTipoDocumento === val);
                                setSubIsFechaEditable(tipoDoc?.fechaEditable || false);
                                if (!tipoDoc?.fechaEditable) setSubFechaVencimiento('');
                            }}
                            classLabel={'col-form-label col-4 text-end'}
                            classDivSelect="col-8"
                        />
                    </div>
                    <div className="row mt-2">
                        <div className="col-md-4 text-end">
                            <label className="col-form-label">Documento</label>
                            <input
                                type="file"
                                onChange={handleSubFileChange}
                                style={{ display: 'none' }}
                                ref={fileInputRef}
                            />
                        </div>
                        <div className="col-md-8">
                            <div className="d-flex align-items-center justify-content-between">
                                <Button
                                    className="btn-secondary pr-2"
                                    variant="secondary"
                                    onClick={() => fileInputRef.current?.click()}
                                >
                                    Examinar
                                </Button>
                                <div>
                                    <input
                                        type="text"
                                        style={{ width: '200px', height: '35px' }}
                                        value={
                                            subSelectedFile
                                                ? subSelectedFile.name
                                                : 'Ningún archivo seleccionado'
                                        }
                                        readOnly
                                    />
                                </div>
                            </div>
                        </div>
                        {subIsFechaEditable && (
                            <div className="row mt-2">
                                <div className="col-md-4 text-end">
                                    <label className="col-form-label">
                                        Fecha Vencimiento{' '}
                                        <span className="text-danger">*</span>
                                    </label>
                                </div>
                                <div className="col-md-8">
                                    <input
                                        type="date"
                                        className="form-control"
                                        value={subFechaVencimiento}
                                        min={new Date().toISOString().split('T')[0]}
                                        onChange={(e) =>
                                            setSubFechaVencimiento(e.target.value)
                                        }
                                        style={{ width: '200px', height: '35px' }}
                                    />
                                </div>
                            </div>
                        )}
                    </div>
                    <div className="row mt-2">
                        <div className="col-md-9"></div>
                        <div className="col-md-3 d-flex justify-content-end">
                            <Button
                                variant="primary"
                                onClick={handleSaveSubformDoc}
                                className="mr-2"
                            >
                                Guardar
                            </Button>
                            <Button
                                variant="primary"
                                onClick={handleCancelDocModal}
                                className="mr-2"
                            >
                                Salir
                            </Button>
                        </div>
                    </div>
                </div>
            </MyModal>

            {/* Formulario principal */}
            <div className="form-horizontal">
                {/* Row 1: Tipo Doc. Persona | Nro. Documento */}
                <div className="row mt-2">
                    <div className="col-6">
                        <div className="row">
                            <FormSelectBox
                                title={'Tipo Doc. Persona'}
                                label={'tipo doc. persona'}
                                id={'idTipoDocPersonaHab'}
                                name={'idTipoDocPersona'}
                                optionId={'id'}
                                optionLabel={'tipoDocumento'}
                                value={form.idTipoDocPersona}
                                options={tiposDocPersona}
                                onChange={(e) =>
                                    handleInputChange(e.target.name, e.target.value)
                                }
                                isDisabled={isDisabled}
                                classLabel={'col-form-label col-5 text-end'}
                                classDivSelect="col-7"
                            />
                        </div>
                    </div>
                    <div className="col-6" onBlur={handleNroDocumentoBlur}>
                        <div className="row">
                            <FormInput
                                id={'nroDocumentoHab'}
                                label={'Nro. Documento'}
                                name={'nroDocumento'}
                                value={form.nroDocumento}
                                isDisabled={isDisabled}
                                classLabel={'col-form-label col-5 text-end'}
                                classColumn={'col-7'}
                                onChange={(e) =>
                                    handleInputChange(e.target.name, e.target.value)
                                }
                            />
                        </div>
                    </div>
                </div>

                {/* Row 2: Nombre | Apellido */}
                <div className="row mt-2">
                    <div className="col-6">
                        <div className="row">
                            <FormInput
                                id={'nombreHab'}
                                label={'Nombre'}
                                name={'nombre'}
                                value={form.nombre}
                                isDisabled={isDisabled || nombreApellidoDisabled}
                                classLabel={'col-form-label col-5 text-end'}
                                classColumn={'col-7'}
                                onChange={(e) =>
                                    handleInputChange(e.target.name, e.target.value)
                                }
                            />
                        </div>
                    </div>
                    <div className="col-6">
                        <div className="row">
                            <FormInput
                                id={'apellidoHab'}
                                label={'Apellido'}
                                name={'apellido'}
                                value={form.apellido}
                                isDisabled={isDisabled || nombreApellidoDisabled}
                                classLabel={'col-form-label col-5 text-end'}
                                classColumn={'col-7'}
                                onChange={(e) =>
                                    handleInputChange(e.target.name, e.target.value)
                                }
                            />
                        </div>
                    </div>
                </div>

                {/* Row 3: Situación Contractual (radios) | Botón Agregar Documento */}
                <div className="row mt-2">
                    <label className="col-form-label col-3 text-end">
                        Situación Contractual
                    </label>
                    <div className="col-5">
                        {SITUACIONES_CONTRACTUALES.map((s) => (
                            <div className="form-check" key={s.id}>
                                <input
                                    type="radio"
                                    className="form-check-input"
                                    name="situacionContractual"
                                    id={`sc_${s.id}`}
                                    value={s.id}
                                    checked={form.situacionContractual === s.id}
                                    disabled={isDisabled}
                                    onChange={(e) =>
                                        handleInputChange('situacionContractual', e.target.value)
                                    }
                                />
                                <label
                                    className="form-check-label"
                                    htmlFor={`sc_${s.id}`}
                                >
                                    {s.descripcion}
                                </label>
                            </div>
                        ))}
                    </div>
                    <div className="col-4 d-flex align-items-start justify-content-end">
                        {!isDisabled && (
                            <Button
                                variant="secondary"
                                size="sm"
                                onClick={() => setIsOpenDocModal(true)}
                            >
                                Agregar Documento
                            </Button>
                        )}
                    </div>
                </div>

                {/* Tabla de documentos */}
                <div className="mt-2">
                    <Table
                        data={allDocRows}
                        columns={docColumns}
                        onCheckboxChange={onCheckboxChange}
                    />
                </div>

                {/* Botones principales */}
                <div className="row mt-3">
                    <div className="col-md-9" />
                    <div className="col-md-3 d-flex justify-content-end gap-1">
                        {!isDisabled && (
                            <Button variant="primary" onClick={handleSave} ref={btnRef}>
                                Guardar
                            </Button>
                        )}
                        <Button variant="primary" onClick={cancelButtonFn}>
                            Salir
                        </Button>
                    </div>
                </div>
            </div>
        </>
    );
};
